// src/components/jobs/JobDetailView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, DollarSign, MapPin, Clock, User, 
  MessageSquare, Star, Edit3, Trash2, CheckCircle, XCircle,
  FileText, Target, Users, Zap, AlertTriangle, Download
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';
import OpenchatWidget from '../chat/OpenchatWidget';

const JobDetailView = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatRecipient, setChatRecipient] = useState(null);


  useEffect(() => {
    loadJobDetails();
  }, [jobId]);

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      const jobData = await jobService.getJobById(jobId);
      setJob(jobData);
    } catch (error) {
      setError('Failed to load job details');
      console.error('Error loading job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await jobService.updateJob(jobId, { status: newStatus });
      setJob(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating job status:', error);
    }
  };

  const handleAcceptApplication = async (vaId) => {
    try {
      await jobService.acceptApplication(jobId, vaId);
      loadJobDetails(); // Reload to get updated data
    } catch (error) {
      console.error('Error accepting application:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline set';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openChat = (recipientId, recipientName, recipientEmail) => {
    setChatRecipient({ id: recipientId, name: recipientName, email: recipientEmail });
    setChatOpen(true);
  };

  const handleStartChat = () => {
    if (job.assignedVA) {
      /* openJobChat(
        job.assignedVA.id,
        job.assignedVA.name,
        job.assignedVA.email,
        {
          id: job.id,
          title: job.title,
          status: job.status
        }
      ); */
    }
  };

  const getUrgencyInfo = (urgency) => {
    switch (urgency) {
      case 'high': 
        return { color: 'text-red-600 bg-red-50', label: 'High Priority', icon: AlertTriangle };
      case 'medium': 
        return { color: 'text-yellow-600 bg-yellow-50', label: 'Medium Priority', icon: Clock };
      case 'low': 
        return { color: 'text-green-600 bg-green-50', label: 'Low Priority', icon: CheckCircle };
      default: 
        return { color: 'text-gray-600 bg-gray-50', label: 'Standard', icon: Clock };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <AlertTriangle size={48} className="mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Job Not Found</h3>
        <p className="text-gray-600 mb-4">{error || 'The job you\'re looking for doesn\'t exist.'}</p>
        <button
          onClick={() => navigate('/jobs/manage')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Back to Jobs
        </button>
      </div>
    );
  }

  const urgencyInfo = getUrgencyInfo(job.urgency);
  const UrgencyIcon = urgencyInfo.icon;
  const isOwner = job.clientId === userProfile.uid;
  const isAssignedVA = job.assignedVA && job.assignedVA.id === userProfile.uid;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/jobs/manage')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Jobs
        </button>
        
        {isOwner && (
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/jobs/edit/${jobId}`)}
              className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              <Edit3 size={16} className="mr-2" />
              Edit Job
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Header */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                    {job.category}
                  </span>
                  <span className={`px-2 py-1 rounded-full font-medium ${getStatusColor(job.status)}`}>
                    {job.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full font-medium flex items-center ${urgencyInfo.color}`}>
                    <UrgencyIcon size={12} className="mr-1" />
                    {urgencyInfo.label}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <DollarSign size={16} className="text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-600">Budget</div>
                  <div className="font-semibold">R{job.budget?.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{job.budgetType}</div>
                </div>
              </div>
              <div className="flex items-center">
                <Calendar size={16} className="text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-600">Deadline</div>
                  <div className="font-semibold">{formatDate(job.deadline)}</div>
                </div>
              </div>
              <div className="flex items-center">
                <MapPin size={16} className="text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-600">Location</div>
                  <div className="font-semibold">{job.location || 'Remote'}</div>
                </div>
              </div>
              <div className="flex items-center">
                <Users size={16} className="text-gray-400 mr-2" />
                <div>
                  <div className="text-sm text-gray-600">Applications</div>
                  <div className="font-semibold">{job.applications?.length || 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText size={20} className="mr-2" />
              Job Description
            </h2>
            <div className="prose prose-sm max-w-none text-gray-700">
              {job.description.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3">{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Requirements & Skills */}
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target size={20} className="mr-2" />
              Requirements & Skills
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {job.skillsRequired?.map(skill => (
                    <span key={skill} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Experience Level</h3>
                <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                  {job.experienceLevel?.charAt(0).toUpperCase() + job.experienceLevel?.slice(1)}
                </span>
              </div>
            </div>

            {job.requirements && job.requirements.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Specific Requirements</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {job.requirements.map((requirement, index) => (
                    <li key={index}>{requirement}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Deliverables */}
          {job.deliverables && job.deliverables.length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle size={20} className="mr-2" />
                Expected Deliverables
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                {job.deliverables.map((deliverable, index) => (
                  <li key={index}>{deliverable}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Applications */}
          {isOwner && job.applications && job.applications.length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Users size={20} className="mr-2" />
                Applications ({job.applications.length})
              </h2>
              <div className="space-y-4">
                {job.applications.map((application, index) => (
                  <ApplicationCard 
                    key={index}
                    application={application}
                    onAccept={() => handleAcceptApplication(application.vaId)}
                    jobStatus={job.status}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          {isOwner && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {job.status === 'open' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate('paused')}
                      className="w-full px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 flex items-center"
                    >
                      <Clock size={16} className="mr-2" />
                      Pause Job
                    </button>
                    <button
                      onClick={() => handleStatusUpdate('cancelled')}
                      className="w-full px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 flex items-center"
                    >
                      <XCircle size={16} className="mr-2" />
                      Cancel Job
                    </button>
                  </>
                )}
                
                {job.status === 'paused' && (
                  <button
                    onClick={() => handleStatusUpdate('open')}
                    className="w-full px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 flex items-center"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Reopen Job
                  </button>
                )}
                
                {job.status === 'in_progress' && (
                  <button
                    onClick={() => handleStatusUpdate('completed')}
                    className="w-full px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 flex items-center"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Job Info */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Job Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Posted</span>
                <span className="font-medium">{formatDate(job.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Updated</span>
                <span className="font-medium">{formatDate(job.updatedAt)}</span>
              </div>
              {job.expectedDuration && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{job.expectedDuration}</span>
                </div>
              )}
              {job.preferredWorkingHours && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Working Hours</span>
                  <span className="font-medium">{job.preferredWorkingHours}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Communication</span>
                <span className="font-medium capitalize">{job.communicationPreference}</span>
              </div>
            </div>
          </div>

          {/* Assigned VA */}
          {job.assignedVA && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Assigned VA</h3>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{job.assignedVA.name}</div>
                  <div className="text-sm text-gray-600">{job.assignedVA.title}</div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Star size={12} className="text-yellow-400 mr-1" />
                    {job.assignedVA.rating} rating
                  </div>
                </div>
              </div>
              {(isOwner || isAssignedVA) && (
              <button
                onClick={() => openChat(
                  isOwner ? job.assignedVA : job.clientId,
                  isOwner ? job.assignedVAName : job.clientName,
                  isOwner ? job.assignedVAEmail : job.clientEmail
                )}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <MessageSquare size={16} className="mr-2" />
                Message
              </button>
            )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Job</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this job? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await jobService.deleteJob(jobId);
                    navigate('/jobs/manage');
                  } catch (error) {
                    console.error('Error deleting job:', error);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Application Card Component
const ApplicationCard = ({ application, onAccept, jobStatus }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User size={16} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{application.vaName}</h4>
            <p className="text-sm text-gray-600">{application.vaTitle}</p>
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <Star size={12} className="text-yellow-400 mr-1" />
              {application.vaRating} • {application.vaCompletedJobs} jobs completed
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-medium text-gray-900">R{application.proposedRate}</div>
          <div className="text-sm text-gray-600">{application.proposedTimeframe}</div>
        </div>
      </div>
      
      {application.coverMessage && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">{application.coverMessage}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between mt-4">
        <span className="text-sm text-gray-500">
          Applied {new Date(application.appliedAt).toLocaleDateString()}
        </span>
        
        {jobStatus === 'open' && application.status === 'pending' && (
          <div className="flex space-x-2">
            <button className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              View Profile
            </button>
            <button 
              onClick={onAccept}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Accept
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailView;